import {
  IconName,
  SubAccountTypeEnum,
  removeEmptyFilters,
  useAssetsFilter,
  usePositionAssetTypesFilter,
  type FilterClause,
  type FilterableProperty,
  type UseFilterBuilderProps,
} from '@talos/kyoko';
import { compact, isEqual, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag, useSubAccountsFilter } from '../../../../hooks';
import type { PositionsTableFilter } from '../types';
import { usePositionsBaseFilter, type UsePositionsBaseFilterParams } from '../usePositionsBaseFilter';

interface UsePositionsSubAccountsFilterParams extends UsePositionsBaseFilterParams {
  defaultFilter: PositionsTableFilter;
  hierarchical: boolean;
}

export const usePositionsSubAccountsFilter = ({
  defaultFilter,
  hierarchical,
  ...params
}: UsePositionsSubAccountsFilterParams) => {
  const { changeFilter, initialFilterClauses, ...baseFilter } = usePositionsBaseFilter(params);
  const { enablePredictionMarkets } = useFeatureFlag();
  const assetsFilter = useAssetsFilter();

  const subAccountsFilter = useSubAccountsFilter({ includeRollups: true });
  const positionAssetTypesFilter = usePositionAssetTypesFilter({ includePredictionMarkets: enablePredictionMarkets });
  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof PositionsTableFilter>>([
        { ...subAccountsFilter, key: 'SubAccounts' },
        { ...assetsFilter, label: 'Instrument' },
        positionAssetTypesFilter,
        {
          key: 'SubAccountTypes',
          label: 'Sub Account Type',
          icon: IconName.TreeDiagram,
          control: 'multi-select',
          options: Object.keys(SubAccountTypeEnum).filter(type =>
            // only books in hierarchical mode
            hierarchical ? type === SubAccountTypeEnum.Book : true
          ),
          getOptionLabel: (option: string) => startCase(option),
        },
      ]),
    [subAccountsFilter, assetsFilter, positionAssetTypesFilter, hierarchical]
  );

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<PositionsTableFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies PositionsTableFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const filterBuilderProps = useMemo(
    () =>
      ({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }) satisfies UseFilterBuilderProps,
    [initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );
  return {
    ...baseFilter,
    filterBuilderProps,
  };
};
